import { AutoComplete, Button, Icons, Input, Tag, Typography } from '@pankod/refine-antd';
import React, { useEffect, useState } from 'react';
import { useDebounce } from 'hooks';
import { getSearchData } from 'services/meilisearch';
import { capitalize } from 'utilities/shared';
import styles from './style.module.css';

const { Text } = Typography;

interface IGroupOption {
  value: string;
  label: string | React.ReactNode;
  key: string;
  id: string;
}

interface IOption {
  label: string | React.ReactNode;
  options: IGroupOption[];
}

export type Group =
  | 'id'
  | 'client'
  | 'supplier'
  | 'user'
  | 'warehouse'
  | 'product'
  | 'tag'
  | 'category'
  | 'sku'
  | 'name'
  | 'clientCode'
  | 'supplierCode'
  | 'email'
  | 'mobileNumber'
  | 'warehouseCode'
  | 'area';

type SearchProps = {
  placeholder: string;
  className?: string;
  style?: React.CSSProperties;
  size: 'small' | 'middle' | 'large';
  index: string;
  groups?: Group[];
  onSelectOption: (searchText: string, ids?: string[]) => void | Promise<void>;
  onReset: () => void | Promise<void>;
  categorySingleShow?: boolean;
  // Fixed entries always offered in the dropdown, independent of the live search
  // index (e.g. a synthetic "Uncategorized" option with no backing search document).
  pinnedOptions?: { id: string; label: string }[];
};

const renderTitle = (title: string) => (
  <Text strong style={{ fontSize: '1rem' }} key={title}>{`${title}`}</Text>
);

const renderItem = (groupName: string, id: string, title: string, index: number): IGroupOption => ({
  // Keep option keys unique across groups to avoid dropdown virtualization collisions.
  key: `${groupName}-${id}-${index}`,
  id,
  value: title,
  label: <Text key={`${groupName}-${id}-${index}`}>{title}</Text>,
});

const getGroupOptions = (groupName: string, options: { id: string; name: string }[]) => {
  const groupOptions: IGroupOption[] = [];

  for (const [index, option] of options.entries()) {
    if (!option.name) continue;
    groupOptions.push(renderItem(groupName, option.id, option.name, index));
  }

  return groupOptions;
};

const getGroupsOptions = async (index: string, searchText: string) => {
  // Get search values from meilisearch
  const searchValues = await getSearchData(index, searchText);

  // Insert meilisearch result into their specific arrays
  const ids: { id: string; name: string }[] = [];
  const clients: { id: string; name: string }[] = [];
  const suppliers: { id: string; name: string }[] = [];
  const users: { id: string; name: string }[] = [];
  const warehouses: { id: string; name: string }[] = [];
  const products: { id: string; name: string }[] = [];
  const tags: { id: string; name: string }[] = [];
  const categories: { id: string; name: string }[] = [];
  const names: { id: string; name: string }[] = [];
  const skus: { id: string; name: string }[] = [];
  const clientCodes: { id: string; name: string }[] = [];
  const supplierCodes: { id: string; name: string }[] = [];
  const emails: { id: string; name: string }[] = [];
  const mobileNumbers: { id: string; name: string }[] = [];
  const areas: { id: string; name: string }[] = [];
  const warehouseCodes: { id: string; name: string }[] = [];

  searchValues.forEach((item) => {
    const itemId = item.id || item.expense_id || item.tag_id || item.category_id || item.product_id;

    itemId && ids.push({ id: itemId, name: itemId });
    item.client &&
      !clients.some((c) => c.name === item.client) &&
      clients.push({ id: itemId, name: item.client });
    item.supplier &&
      !suppliers.some((s) => s.name === item.supplier) &&
      suppliers.push({ id: itemId, name: item.supplier });
    item.user &&
      !users.some((u) => u.name === item.user) &&
      users.push({ id: itemId, name: item.user });
    item.name &&
      !names.some((n) => n.name === item.name) &&
      names.push({ id: itemId, name: item.name });
    item.sku &&
      !skus.some((s) => s.name === item.sku) &&
      skus.push({ id: itemId, name: item.sku });
    item.clientCode &&
      !clientCodes.some((c) => c.name === item.clientCode) &&
      clientCodes.push({ id: itemId, name: item.clientCode });
    item.supplierCode &&
      !supplierCodes.some((s) => s.name === item.supplierCode) &&
      supplierCodes.push({ id: itemId, name: item.supplierCode });
    item.email &&
      !emails.some((e) => e.name === item.email) &&
      emails.push({ id: itemId, name: item.email });
    item.mobileNumber &&
      !mobileNumbers.some((m) => m.name === item.mobileNumber) &&
      mobileNumbers.push({ id: itemId, name: item.mobileNumber });
    item.area &&
      !areas.some((a) => a.name === item.area) &&
      areas.push({ id: itemId, name: item.area });
    item.warehouseCode &&
      !warehouseCodes.some((w) => w.name === item.warehouseCode) &&
      warehouseCodes.push({ id: itemId, name: item.warehouseCode });
    item.category &&
      !Array.isArray(item.category) &&
      !categories.some((c) => c.name === item.category) &&
      categories.push({ id: itemId, name: item.category });

    item.warehouse &&
      !Array.isArray(item.warehouse) &&
      !warehouses.some((w) => w.name === item.warehouse) &&
      warehouses.push({ id: itemId, name: item.warehouse });

    if (Array.isArray(item.warehouse)) {
      item.warehouse.forEach((warehouse: string) => {
        if (!warehouses.some((w) => w.name === warehouse)) {
          warehouses.push({ id: itemId, name: warehouse });
        }
      });
    }
    if (Array.isArray(item.products)) {
      item.products.forEach((product: string) => {
        if (!products.some((p) => p.name === product)) {
          products.push({ id: itemId, name: product });
        }
      });
    }
    if (Array.isArray(item.tags)) {
      item.tags.forEach((tag: string) => {
        if (!tags.some((t) => t.name === tag)) {
          tags.push({ id: itemId, name: tag });
        }
      });
    }
    if (Array.isArray(item.category)) {
      item.category.forEach((category: string) => {
        if (!categories.some((c) => c.name === category)) {
          categories.push({ id: itemId, name: category });
        }
      });
    }
    if (Array.isArray(item.categories)) {
      item.categories.forEach((category: string) => {
        if (!categories.some((c) => c.name === category)) {
          categories.push({ id: itemId, name: category });
        }
      });
    }
  });

  // Create option group object
  const groupsOptions: Record<string, IGroupOption[]> = {
    id: getGroupOptions('id', ids),
    client: getGroupOptions('client', clients),
    supplier: getGroupOptions('supplier', suppliers),
    user: getGroupOptions('user', users),
    warehouse: getGroupOptions('warehouse', warehouses),
    product: getGroupOptions('product', products),
    tag: getGroupOptions('tag', tags),
    name: getGroupOptions('name', names),
    sku: getGroupOptions('sku', skus),
    category: getGroupOptions('category', categories),
    clientCode: getGroupOptions('clientCode', clientCodes),
    supplierCode: getGroupOptions('supplierCode', supplierCodes),
    email: getGroupOptions('email', emails),
    mobileNumber: getGroupOptions('mobileNumber', mobileNumbers),
    area: getGroupOptions('area', areas),
    warehouseCode: getGroupOptions('warehouseCode', warehouseCodes),
  };

  return groupsOptions;
};

export const SearchNew: React.FC<SearchProps> = (props) => {
  const [searchText, setSearchText] = useState<string>();
  const [selectedItems, setSelectedItems] = useState<{ id: string; name: string }[]>([]);
  const [options, setOptions] = useState<IOption[]>([]);

  const debouncedSearchText = useDebounce(searchText, 500);

  const pinnedGroup: IOption[] = props.pinnedOptions?.length
    ? [
        {
          label: renderTitle('Fixed'),
          options: props.pinnedOptions.map((opt, index) =>
            renderItem('fixed', opt.id, opt.label, index)
          ),
        },
      ]
    : [];

  useEffect(() => {
    (async () => {
      if (debouncedSearchText) {
        const groupsOptions = await getGroupsOptions(props.index, debouncedSearchText);

        const options: IOption[] = [];
        if (!props.groups || (Array.isArray(props.groups) && !props.groups.length)) {
          for (const key in groupsOptions) {
            options.push({
              label: renderTitle(capitalize(key)),
              options: groupsOptions[key],
            });
          }
        }
        if (Array.isArray(props.groups) && props.groups.length) {
          for (const value of props.groups) {
            options.push({
              label: renderTitle(capitalize(value)),
              options: groupsOptions[value],
            });
          }
        }

        setOptions([...pinnedGroup, ...options]);
        return;
      }

      setOptions(pinnedGroup);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText]);

  useEffect(() => {
    props.onSelectOption(
      selectedItems.map((item) => item.name).join(', '),
      selectedItems.map((item) => item.id)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  const onChange = async (value: string) => {
    setSearchText(value);
  };

  const onSelect = (value: string, option: IGroupOption) => {
    if (props.categorySingleShow) {
      setSelectedItems([{ id: option.id, name: value }]);
      setSearchText('');
      return;
    }
    if (!selectedItems.some((item) => item.name === value)) {
      setSelectedItems((prev) => [...prev, { id: option.id, name: value }]);
    }
    setSearchText('');
  };

  const submitSearchText = () => {
    const value = searchText?.trim();
    if (!value) return;

    if (props.categorySingleShow) {
      setSelectedItems([{ id: value, name: value }]);
      setSearchText('');
      return;
    }

    if (!selectedItems.some((item) => item.name === value)) {
      setSelectedItems((prev) => [...prev, { id: value, name: value }]);
    }
    setSearchText('');
  };

  const handleCloseTag = (removedItem: { id: string; name: string }) => {
    const filteredItems = selectedItems.filter((item) => item.id !== removedItem.id);
    setSelectedItems(filteredItems);
  };

  const onResetClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setSelectedItems([]);
    props.onReset();
  };

  const renderedTags = selectedItems.map((item, index) => {
    return (
      <Tag
        key={`${item.id}-${index}`}
        color="blue"
        closable
        onClose={(e) => {
          e.preventDefault();
          handleCloseTag(item);
        }}
      >
        {item.name}
      </Tag>
    );
  });

  return (
    <div className={styles['search-section']}>
      <div className={props.className} style={{ width: '100%' }}>
        <AutoComplete
          style={{ width: '100%' }}
          value={searchText}
          options={options as any}
          onSelect={onSelect}
          onChange={onChange}
          suffixIcon={<Icons.ReloadOutlined />}
        >
          <Input
            size={props.size}
            placeholder={props.placeholder}
            onPressEnter={submitSearchText}
            suffix={
              <Icons.SearchOutlined onClick={submitSearchText} style={{ cursor: 'pointer' }} />
            }
          />
        </AutoComplete>
        {!!selectedItems.length && (
          <Button
            size={props.size}
            onClick={onResetClick}
            icon={<Icons.ReloadOutlined />}
            style={{ marginLeft: '0.5rem' }}
          />
        )}
      </div>

      <div style={{ marginTop: '1rem' }}>{renderedTags}</div>
    </div>
  );
};
